import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import avatarLeft from '../../../assets/avatarleft-icon.png';
import avatarMiddle from '../../../assets/avatarmiddle-icon.png';
import avatarRight from '../../../assets/avatarright-icon.png';

import './AvatarStudioAwarenessDrawer.css';

export default function AvatarStudioAwarenessDrawer({
  open = false,
  onStart,
  onDismiss,
  onShown,
}) {
  const dialogRef = useRef(null);
  const startRef = useRef(null);

  const callbacksRef = useRef({
    onStart,
    onDismiss,
    onShown,
  });

  callbacksRef.current = {
    onStart,
    onDismiss,
    onShown,
  };

  useEffect(() => {
    if (!open) return undefined;

    const previousFocus = document.activeElement;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousOverflow = document.body.style.overflow;

    let cancelled = false;
    let shown = false;
    let frameId = null;

    if (root) root.inert = true;

    document.body.style.overflow = 'hidden';
    startRef.current?.focus({ preventScroll: true });

    // Wait for a visible document and a painted drawer before
    // reporting an impression.
    function scheduleShown() {
      if (
        cancelled ||
        shown ||
        frameId !== null ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = window.requestAnimationFrame(() => {
          frameId = null;

          if (
            cancelled ||
            shown ||
            document.visibilityState !== 'visible' ||
            !dialogRef.current
          ) {
            return;
          }

          shown = true;
          callbacksRef.current.onShown?.();
        });
      });
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        callbacksRef.current.onDismiss?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const buttons = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled])'
        ) || []
      );

      const first = buttons[0];
      const last = buttons[buttons.length - 1];

      if (!first) return;

      const focusInside = dialogRef.current?.contains(
        document.activeElement
      );

      if (
        event.shiftKey &&
        (!focusInside || document.activeElement === first)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (!focusInside || document.activeElement === last)
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('visibilitychange', scheduleShown);

    scheduleShown();

    return () => {
      cancelled = true;

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      document.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );

      document.removeEventListener(
        'visibilitychange',
        scheduleShown
      );

      document.body.style.overflow = previousOverflow;

      if (root) root.inert = previousInert;

      if (previousFocus?.isConnected) {
        previousFocus.focus?.({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="avatar-awareness"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          callbacksRef.current.onDismiss?.();
        }
      }}
    >
      <section
        ref={dialogRef}
        className="avatar-awareness__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-awareness-title"
      >
        <div
          className="avatar-awareness__handle"
          aria-hidden="true"
        />

        <h2
          id="avatar-awareness-title"
          className="avatar-awareness__title"
        >
          Create your avatar
        </h2>

        <div
          className="avatar-awareness__artwork"
          aria-hidden="true"
        >
          <img
            src={avatarLeft}
            alt=""
            className="avatar-awareness__avatar avatar-awareness__avatar--left"
            draggable={false}
          />

          <img
            src={avatarMiddle}
            alt=""
            className="avatar-awareness__avatar avatar-awareness__avatar--middle"
            draggable={false}
          />

          <img
            src={avatarRight}
            alt=""
            className="avatar-awareness__avatar avatar-awareness__avatar--right"
            draggable={false}
          />
        </div>

        <div className="avatar-awareness__actions">
          <button
            ref={startRef}
            type="button"
            className="avatar-awareness__start"
            onClick={() => callbacksRef.current.onStart?.()}
          >
            Start
          </button>

          <button
            type="button"
            className="avatar-awareness__later"
            onClick={() => callbacksRef.current.onDismiss?.()}
          >
            Maybe later
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}
